import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
    BroadcastEnvironment,
    BroadcastImportEvent,
    ChatEvent,
    Color,
    GameEnvironment,
    GameResult,
    GameServerStatus,
    Instruments,
    Move,
    Observer,
    PlayerInfo,
    PlayerState,
    StoneRemoval,
    TimeState,
    TInstruments,
    Undo,
} from './types';
import GameTree from '@sabaki/immutable-gametree';
import { GameTree as TGameTree } from '@/shared/ui/GOViewer/model/types';
import { formatCoordinates } from '@/shared/ui/GOViewer/model/utils';
import {
    appendNodeToTree,
    buildTreeFromBroadcast,
    getBroadcastMainLineEndId,
    getCurrentMove,
    getRootGameProperties,
    getRootTournamentProperties,
    METADATA_TO_SGF,
    replaceRootGameProperties,
} from './utils';
import { BroadcastBroadcastSource, BroadcastTournament } from '@/api';

interface UpdatePlayersPayload {
    black_player: PlayerState;
    white_player: PlayerState;
}

interface UpdatePlayersInfoPayload {
    black_player: Partial<PlayerInfo>;
    white_player: Partial<PlayerInfo>;
}

interface GameInfo {
    gameId?: string;
    myColor: -1 | 0 | 1;
    userId?: string;
    tournamentId: string | null;
    type: 'broadcast' | 'game' | null;
    isPrivate: boolean;
    allowedUsers: string[];
    metaLoaded: boolean;
    tournament: BroadcastTournament | null;
    broadcastPlayerRanks: {
        black?: string;
        white?: string;
    };
    createdAt?: string;
    description?: string;
    source?: BroadcastBroadcastSource;
}

interface GameStore {
    game: Omit<GameEnvironment, 'moves'> | null;
    tree: TGameTree;
    currentId: number | null;
    bidToGameNodeId: Map<number, number>;
    gameInfo: GameInfo;
    instruments: Instruments | null;
    reset: () => void;
    setGameId: (id: string) => void;
    setUserId: (id: string) => void;
    initGame: (data: GameEnvironment) => void;
    initBroadcast: (data: BroadcastEnvironment) => void;
    importBroadcast: (data: BroadcastImportEvent) => void;
    initFromTree: (tree: TGameTree, currentId: number, game: Omit<GameEnvironment, 'moves'>) => void;
    addMove: (data: { move: Move; black_time: TimeState; white_time: TimeState; node_id?: number }) => void;
    addPass: (data: { color: Color; black_time: TimeState; white_time: TimeState }) => void;
    updatePlayers: (payload: UpdatePlayersPayload) => void;
    updatePlayersInfo: (payload: UpdatePlayersInfoPayload) => void;
    updateGameStatus: (payload: GameServerStatus) => void;
    updateTime: (data: { black_time: TimeState; white_time: TimeState }) => void;
    addObserver: (observer: Observer) => void;
    removeObserver: (observer: Observer) => void;
    addMessage: (message: ChatEvent) => void;
    setGameResult: (payload: { result: GameResult }) => void;
    setStoneRemoval: (data: StoneRemoval) => void;
    requestUndo: (data: Omit<Undo, 'accept'>) => void;
    setUndo: (data: Omit<Undo, 'timeout_sec'>) => void;
    sending: boolean;
    setSending: (value: boolean) => void;
    setCurrentBroadcastNode: (broadcastNodeId: number) => void;
    cutBroadcastNode: (broadcastNodeId: number) => void;
    addBroadcastPass: (data: { color: Color; node_id: number }) => void;
    setActiveInstrument: (data: TInstruments) => void;
    initInstruments: (data: TInstruments[]) => void;
    setPlayerRank: (color: 'white' | 'black', rank: string) => void;
    setGameDescription: (description: string) => void;
    setTournamentRound: (round: number) => void;
    applyNodeMetadata: (data: { node_id: number; diff: Record<string, string> }) => void;
    setBroadcastMeta: (meta: {
        tournamentId: string | null;
        isPrivate: boolean;
        allowedUsers: string[];
        tournament: BroadcastTournament | null;
        playerRanks?: {
            black?: string;
            white?: string;
        };
        createdAt?: string;
        description?: string;
        source?: BroadcastBroadcastSource;
    }) => void;
}

const initState = {
    game: null,
    tree: new GameTree() as TGameTree,
    currentId: null,
    bidToGameNodeId: new Map<number, number>(),
    gameInfo: {
        myColor: 0 as -1 | 0 | 1,
        tournamentId: null,
        type: null,
        isPrivate: false,
        allowedUsers: [] as string[],
        metaLoaded: false,
        tournament: null,
        broadcastPlayerRanks: {},
    },
    instruments: null,
    sending: false,
};

function applyPlayerTimes(
    game: Omit<GameEnvironment, 'moves'>,
    black_time: TimeState,
    white_time: TimeState
): Omit<GameEnvironment, 'moves'> {
    return {
        ...game,
        black_player: { ...game.black_player, time: black_time },
        white_player: { ...game.white_player, time: white_time },
    };
}

export const useGameStore = create<GameStore>()(
    devtools((set) => ({
        ...initState,

        setGameId: (id) => set((state) => ({ gameInfo: { ...state.gameInfo, gameId: id } }), false, 'game/setGameId'),

        setUserId: (id) => set((state) => ({ gameInfo: { ...state.gameInfo, userId: id } }), false, 'game/setUserId'),

        setBroadcastMeta: ({
            tournamentId,
            isPrivate,
            allowedUsers,
            tournament,
            playerRanks,
            createdAt,
            description,
            source,
        }) =>
            set(
                (state) => ({
                    gameInfo: {
                        ...state.gameInfo,
                        tournamentId,
                        isPrivate,
                        allowedUsers,
                        metaLoaded: true,
                        createdAt,
                        description: description ?? state.gameInfo.description,
                        source: source ?? state.gameInfo.source,
                        tournament,
                        broadcastPlayerRanks: playerRanks ?? state.gameInfo.broadcastPlayerRanks,
                    },
                }),
                false,
                'game/setBroadcastMeta'
            ),

        initGame: (data) =>
            set(
                (state) => {
                    const { moves, ...restGameData } = data;

                    let tree = state.tree;
                    let lastId = tree.root.id;

                    if (moves) {
                        for (const m of moves) {
                            const coord = formatCoordinates([m.c, m.r]);
                            ({ tree, lastId } = appendNodeToTree(tree, lastId, m.col, coord));
                        }
                    }

                    tree = tree.mutate((draft) => {
                        draft.addToProperty(draft.root.id, 'SZ', `${data.settings.board_size}`);
                        replaceRootGameProperties(
                            draft,
                            getRootGameProperties(data.settings, data.black_player_info, data.white_player_info)
                        );
                        data.settings.handicap && draft.addToProperty(draft.root.id, 'H', `${data.settings.handicap}`);
                        if (data.handicap_stones && data.handicap_stones.length > 0) {
                            data.handicap_stones.forEach((stone) => {
                                draft.addToProperty(draft.root.id, 'AB', formatCoordinates([stone.c, stone.r]));
                            });
                        }
                    });

                    let myColor: -1 | 0 | 1 = 0;
                    if (state.gameInfo?.userId) {
                        if (data.black_player_info.id === state.gameInfo.userId) myColor = 1;
                        if (data.white_player_info.id === state.gameInfo.userId) myColor = -1;
                    }

                    return {
                        game: restGameData,
                        tree,
                        currentId: lastId,
                        gameInfo: { ...state.gameInfo, myColor, type: 'game' as const },
                    };
                },
                false,
                'game/init'
            ),

        initBroadcast: (data) =>
            set(
                (state) => {
                    const blackPlayer = {
                        ...data.black_player,
                        rank: data.black_player.rank || state.gameInfo.broadcastPlayerRanks.black,
                    };
                    const whitePlayer = {
                        ...data.white_player,
                        rank: data.white_player.rank || state.gameInfo.broadcastPlayerRanks.white,
                    };
                    const {
                        tree: rawTree,
                        currentId,
                        bidToGameNodeId,
                    } = buildTreeFromBroadcast(data.tree, data.current_node);

                    let tree = rawTree.mutate((draft) => {
                        draft.addToProperty(draft.root.id, 'SZ', `${data.settings.board_size}`);
                        replaceRootGameProperties(draft, {
                            ...getRootGameProperties(data.settings, blackPlayer, whitePlayer),
                            ...getRootTournamentProperties(state.gameInfo.tournament, state.gameInfo.createdAt),
                        });
                        if (data.settings.handicap) {
                            draft.addToProperty(draft.root.id, 'H', `${data.settings.handicap}`);
                        }
                        if (data.handicap_stones && data.handicap_stones.length > 0) {
                            data.handicap_stones.forEach((stone) => {
                                draft.addToProperty(draft.root.id, 'AB', formatCoordinates([stone.c, stone.r]));
                            });
                        }
                    });

                    if (data.chat && data.chat.length > 0) {
                        const chatByNode = new Map<number, string[]>();
                        for (const msg of data.chat) {
                            const bid = msg.node_id ?? 0;
                            const gameNodeId = bidToGameNodeId.get(bid) ?? (tree.root.id as number);
                            const list = chatByNode.get(gameNodeId) ?? [];
                            list.push(JSON.stringify(msg));
                            chatByNode.set(gameNodeId, list);
                        }
                        tree = tree.mutate((draft) => {
                            for (const [nodeId, msgs] of chatByNode) {
                                msgs.forEach((s) => draft.addToProperty(nodeId, '_CHAT', s));
                            }
                        });
                    }

                    const turn: Color = getCurrentMove({ tree, currentId })?.sign === -1 ? 2 : 1;

                    const game: Omit<GameEnvironment, 'moves'> = {
                        state: data.result ? 'over' : 'playing',
                        result: data.result,
                        settings: data.settings,
                        protocol_settings: {
                            keep_alive_interval_sec: 0,
                            max_undo_count: 0,
                            max_pass_count: 0,
                            max_ai_referee_count: 0,
                            max_ai_referee_moves: {},
                            max_request_counting_count: 0,
                            request_undo_timeout_sec: 0,
                            preparing_timeout_sec: 0,
                            request_counting_timeout_sec: 0,
                            stone_removal_timeout_sec: 0,
                            mutual_disconnection_timeout_sec: 0,
                        },
                        time_control: { type: 'none', mode: 'live', settings: {} },
                        handicap_stones: data.handicap_stones,
                        turn,
                        black_player_info: blackPlayer,
                        white_player_info: whitePlayer,
                        broadcaster_info: data.broadcaster,
                        black_player: {},
                        white_player: {},
                        observers: data.observers ?? [],
                        chat: data.chat ?? [],
                    };

                    let myColor: -1 | 0 | 1 = 0;
                    if (state.gameInfo?.userId) {
                        if (data.black_player.id === state.gameInfo.userId) myColor = 1;
                        if (data.white_player.id === state.gameInfo.userId) myColor = -1;
                    }

                    return {
                        game,
                        tree,
                        currentId,
                        bidToGameNodeId,
                        gameInfo: { ...state.gameInfo, myColor, type: 'broadcast' as const },
                    };
                },
                false,
                'game/initBroadcast'
            ),

        importBroadcast: (data) =>
            set(
                (state) => {
                    if (!state.game) return state;

                    const settings = data.settings ?? state.game.settings;
                    // Legacy import events omit settings and retain the previous handicap.
                    const handicapStones =
                        data.handicap_stones ??
                        (!data.settings && !data.tree.setup ? state.game.handicap_stones : undefined);
                    const blackPlayer = { ...state.game.black_player_info, nick: data.black_nick };
                    const whitePlayer = { ...state.game.white_player_info, nick: data.white_nick };
                    const targetNodeId = getBroadcastMainLineEndId(data.tree);
                    const {
                        tree: rawTree,
                        currentId,
                        bidToGameNodeId,
                    } = buildTreeFromBroadcast(data.tree, targetNodeId);

                    let tree = rawTree.mutate((draft) => {
                        draft.addToProperty(draft.root.id, 'SZ', `${settings.board_size}`);
                        replaceRootGameProperties(draft, {
                            ...getRootGameProperties(settings, blackPlayer, whitePlayer),
                            ...getRootTournamentProperties(state.gameInfo.tournament, state.gameInfo.createdAt),
                        });
                        if (settings.handicap) draft.addToProperty(draft.root.id, 'H', `${settings.handicap}`);
                        if (handicapStones?.length) {
                            handicapStones.forEach((stone) => {
                                draft.addToProperty(draft.root.id, 'AB', formatCoordinates([stone.c, stone.r]));
                            });
                        }
                    });

                    if (state.game.chat?.length) {
                        tree = tree.mutate((draft) => {
                            state.game!.chat.forEach((message) => {
                                if (message.node_id === undefined) return;
                                const gameNodeId = bidToGameNodeId.get(message.node_id);
                                if (gameNodeId !== undefined) {
                                    draft.addToProperty(gameNodeId, '_CHAT', JSON.stringify(message));
                                }
                            });
                        });
                    }

                    return {
                        tree,
                        currentId,
                        bidToGameNodeId,
                        game: {
                            ...state.game,
                            settings,
                            handicap_stones: handicapStones,
                            turn: getCurrentMove({ tree, currentId })?.sign === -1 ? 2 : 1,
                            black_player_info: blackPlayer,
                            white_player_info: whitePlayer,
                        },
                    };
                },
                false,
                'game/importBroadcast'
            ),

        initFromTree: (tree, currentId, game) =>
            set(
                (state) => {
                    let myColor: -1 | 0 | 1 = 0;
                    if (state.gameInfo?.userId) {
                        if (game.black_player_info.id === state.gameInfo.userId) myColor = 1;
                        if (game.white_player_info.id === state.gameInfo.userId) myColor = -1;
                    }
                    return { game, tree, currentId, gameInfo: { ...state.gameInfo, myColor } };
                },
                false,
                'game/initFromTree'
            ),

        addMove: ({ move, black_time, white_time, node_id: topNodeId }) =>
            set(
                (state) => {
                    const effectiveNodeId = move.node_id ?? topNodeId;
                    if (effectiveNodeId !== undefined && state.bidToGameNodeId.has(effectiveNodeId)) {
                        return {
                            game: state.game
                                ? { ...applyPlayerTimes(state.game, black_time, white_time), undo: undefined }
                                : null,
                        };
                    }

                    const coord = formatCoordinates([move.c, move.r]);
                    const { tree: appendedTree, lastId } = appendNodeToTree(
                        state.tree,
                        state.currentId ?? state.tree.root.id,
                        move.col,
                        coord
                    );
                    const tree =
                        effectiveNodeId !== undefined
                            ? appendedTree.mutate((draft) => {
                                  draft.addToProperty(lastId, '_BID', String(effectiveNodeId));
                              })
                            : appendedTree;
                    const newBidToGameNodeId =
                        effectiveNodeId !== undefined
                            ? new Map(state.bidToGameNodeId).set(effectiveNodeId, lastId)
                            : state.bidToGameNodeId;

                    return {
                        tree,
                        currentId: lastId,
                        bidToGameNodeId: newBidToGameNodeId,
                        game: state.game
                            ? { ...applyPlayerTimes(state.game, black_time, white_time), undo: undefined }
                            : null,
                    };
                },
                false,
                'game/addMove'
            ),

        addPass: ({ color, black_time, white_time }) =>
            set(
                (state) => {
                    const { tree, lastId } = appendNodeToTree(
                        state.tree,
                        state.currentId ?? state.tree.root.id,
                        color,
                        ''
                    );
                    return {
                        tree,
                        currentId: lastId,
                        game: state.game
                            ? { ...applyPlayerTimes(state.game, black_time, white_time), undo: undefined }
                            : null,
                    };
                },
                false,
                'game/addPass'
            ),

        updatePlayers: (payload) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    return {
                        game: {
                            ...state.game,
                            black_player: payload.black_player,
                            white_player: payload.white_player,
                        },
                    };
                },
                false,
                'game/updatePlayers'
            ),

        updatePlayersInfo: (payload) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    return {
                        game: {
                            ...state.game,
                            black_player_info: { ...state.game.black_player_info, ...payload.black_player },
                            white_player_info: { ...state.game.white_player_info, ...payload.white_player },
                        },
                    };
                },
                false,
                'game/updatePlayersInfo'
            ),

        updateGameStatus: (payload) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    return { game: { ...state.game, state: payload } };
                },
                false,
                'game/updateGameStatus'
            ),

        updateTime: ({ black_time, white_time }) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    return { game: applyPlayerTimes(state.game, black_time, white_time) };
                },
                false,
                'game/updateTime'
            ),

        addObserver: (observer) =>
            set(
                (state) => {
                    if (!state.game) return state;

                    const currentObservers = state.game.observers ?? [];
                    if (currentObservers.some((obs) => obs.id === observer.id)) return state;

                    return {
                        game: {
                            ...state.game,
                            observers: [...currentObservers, observer],
                            black_player: {
                                ...state.game.black_player,
                                online:
                                    state.game.black_player_info?.id === observer.id
                                        ? true
                                        : state.game.black_player.online,
                            },
                            white_player: {
                                ...state.game.white_player,
                                online:
                                    state.game.white_player_info?.id === observer.id
                                        ? true
                                        : state.game.white_player.online,
                            },
                        },
                    };
                },
                false,
                'game/addObserver'
            ),

        removeObserver: (observer) =>
            set(
                (state) => {
                    if (!state.game?.observers) return state;

                    return {
                        game: {
                            ...state.game,
                            observers: state.game.observers.filter((obs) => obs.id !== observer.id),
                            black_player: {
                                ...state.game.black_player,
                                online:
                                    observer.id === state.game.black_player_info.id
                                        ? false
                                        : state.game.black_player.online,
                            },
                            white_player: {
                                ...state.game.white_player,
                                online:
                                    observer.id === state.game.white_player_info.id
                                        ? false
                                        : state.game.white_player.online,
                            },
                        },
                    };
                },
                false,
                'game/removeObserver'
            ),

        setGameResult: (payload) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    return { game: { ...state.game, state: 'over', result: payload.result } };
                },
                false,
                'game/setGameResult'
            ),

        addMessage: (message) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    const newGame = { game: { ...state.game, chat: [...(state.game.chat ?? []), message] } };
                    if (message.node_id !== undefined) {
                        const gameNodeId = state.bidToGameNodeId.get(message.node_id);
                        if (gameNodeId !== undefined) {
                            const newTree = state.tree.mutate((draft) => {
                                draft.addToProperty(gameNodeId, '_CHAT', JSON.stringify(message));
                            });
                            return { ...newGame, tree: newTree };
                        }
                    }
                    return newGame;
                },
                false,
                'game/addMessage'
            ),

        setStoneRemoval: (data) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    return { game: { ...state.game, stone_removal: data, state: 'stone_removal' } };
                },
                false,
                'game/stoneRemoval'
            ),

        requestUndo: (data) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    return {
                        game: {
                            ...state.game,
                            undo: state.game.undo ? { ...state.game.undo, ...data } : data,
                        },
                    };
                },
                false,
                'game/requestUndo'
            ),

        setUndo: (data) =>
            set(
                (state) => {
                    if (!state.game) return state;

                    let tree = state.tree;
                    let lastId = state.currentId;

                    if (data.accept && state.currentId) {
                        const node = tree.navigate(state.currentId, -1, {});
                        tree = tree.mutate((draft) => {
                            if (state.currentId) draft.removeNode(state.currentId);
                        });
                        lastId = node ? node.id : lastId;
                    }

                    return {
                        game: {
                            ...state.game,
                            undo: state.game.undo ? { ...state.game.undo, ...data, player_id: undefined } : data,
                        },
                        currentId: lastId,
                        tree,
                    };
                },
                false,
                'game/setUndo'
            ),

        addBroadcastPass: ({ color, node_id }) =>
            set(
                (state) => {
                    if (state.bidToGameNodeId.has(node_id)) return state;

                    const { tree: appendedTree, lastId } = appendNodeToTree(
                        state.tree,
                        state.currentId ?? state.tree.root.id,
                        color,
                        ''
                    );
                    const tree = appendedTree.mutate((draft) => {
                        draft.addToProperty(lastId, '_BID', String(node_id));
                    });
                    const newBidToGameNodeId = new Map(state.bidToGameNodeId).set(node_id, lastId);
                    return { tree, currentId: lastId, bidToGameNodeId: newBidToGameNodeId };
                },
                false,
                'game/addBroadcastPass'
            ),

        setCurrentBroadcastNode: (broadcastNodeId) =>
            set(
                (state) => {
                    const gameNodeId = state.bidToGameNodeId.get(broadcastNodeId);
                    if (gameNodeId === undefined) return state;
                    return { currentId: gameNodeId };
                },
                false,
                'game/setCurrentBroadcastNode'
            ),

        cutBroadcastNode: (broadcastNodeId) =>
            set(
                (state) => {
                    const parentGameNodeId = state.bidToGameNodeId.get(broadcastNodeId);
                    if (parentGameNodeId === undefined) return state;

                    const cutNodeId = state.currentId;
                    if (cutNodeId === null || cutNodeId === parentGameNodeId) {
                        return { currentId: parentGameNodeId };
                    }

                    const deletedIds = new Set<number>();
                    const collect = (id: number) => {
                        deletedIds.add(id);
                        state.tree.get(id)?.children.forEach((c) => collect(c.id as number));
                    };
                    collect(cutNodeId);

                    const tree = state.tree.mutate((draft: { removeNode: (id: number) => void }) =>
                        draft.removeNode(cutNodeId)
                    );

                    const newBidToGameNodeId = new Map(state.bidToGameNodeId);
                    for (const [bid, gid] of state.bidToGameNodeId) {
                        if (deletedIds.has(gid)) newBidToGameNodeId.delete(bid);
                    }

                    return { tree, currentId: parentGameNodeId, bidToGameNodeId: newBidToGameNodeId };
                },
                false,
                'game/cutBroadcastNode'
            ),

        setActiveInstrument: (instrument) =>
            set(
                (state) => ({
                    instruments: state.instruments
                        ? { ...state.instruments, active: instrument }
                        : { active: instrument },
                }),
                false,
                'game/setActiveInstrument'
            ),

        initInstruments: (instruments) =>
            set({ instruments: { instrumentList: instruments, active: 'move' } }, false, 'game/initInstruments'),

        setSending: (value) => set({ sending: value }, false, 'game/setSending'),

        setPlayerRank: (color, rank) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    const key = color === 'black' ? 'black_player_info' : 'white_player_info';
                    return {
                        game: {
                            ...state.game,
                            [key]: { ...state.game[key], rank },
                        },
                    };
                },
                false,
                'game/setPlayerRank'
            ),

        setGameDescription: (description) =>
            set(
                (state) => {
                    if (!state.game) return state;
                    return { game: { ...state.game, settings: { ...state.game.settings, description } } };
                },
                false,
                'game/setGameDescription'
            ),

        setTournamentRound: (round) =>
            set(
                (state) => ({
                    gameInfo: {
                        ...state.gameInfo,
                        tournament: state.gameInfo.tournament ? { ...state.gameInfo.tournament, round } : null,
                    },
                    tree: state.tree.mutate((draft) => replaceRootGameProperties(draft, { RO: String(round) })),
                }),
                false,
                'game/setTournamentRound'
            ),

        applyNodeMetadata: ({ node_id, diff }) =>
            set(
                (state) => {
                    const gameNodeId = state.bidToGameNodeId.get(node_id);
                    if (gameNodeId === undefined) return state;

                    let tree = state.tree;
                    for (const [key, jsonValue] of Object.entries(diff)) {
                        const sgfProp = METADATA_TO_SGF[key];
                        if (!sgfProp) continue;
                        const values: string[] = JSON.parse(jsonValue);
                        const existing = (tree.get(gameNodeId)?.data[sgfProp] as string[] | undefined) ?? [];
                        tree = tree.mutate((draft) => {
                            existing.forEach((v) => draft.removeFromProperty(gameNodeId, sgfProp, v));
                            values.forEach((v) => draft.addToProperty(gameNodeId, sgfProp, v));
                        });
                    }

                    return { tree };
                },
                false,
                'game/applyNodeMetadata'
            ),

        reset: () => set(initState, false, 'game/reset'),
    }))
);
